import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IInterventionRequest } from "@formant/core/model/IInterventionRequest";

import { eventStore, userStore } from "../../../stores";
import { captureError } from "../../../utils/captureError";
import { formatDateTime } from "../../../utils/formatDateTime";
import { Form } from "../../common/Form";
import { StandardButton } from "../../common/StandardButton";
import { AssetImage } from "../AssetImage";
import * as styles from "./SelectionRequestDetails.scss";
import { SplitView } from "./SplitView";

export interface ISelectionRequestDetailsProps {
    request: IInterventionRequest<"selection">;
    onRespond: () => void;
}

@observer
export class SelectionRequestDetails extends Component<ISelectionRequestDetailsProps> {
    @observable
    private value?: number;
    @observable
    private loading: boolean = false;

    public render() {
        const { onSubmit, onLoading } = this;
        const { request } = this.props;
        const { data } = request;

        const response = request.responses?.slice(-1)[0];
        const responseUser = response?.userId && userStore.get(response.userId);
        const responseTime = response?.createdAt;

        const summary = (
            <div className={styles.request}>
                <p className={styles.instructions}>{data.instruction}</p>
                {responseUser && responseTime && (
                    <div className={styles["response-details"]}>
                        Completed at{" "}
                        {formatDateTime(parseIsoDate(responseTime))} by{" "}
                        {responseUser.firstName} {responseUser.lastName}.
                    </div>
                )}
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    {data.options.map((option, i) => (
                        <label key={option}>
                            <div className="label">{option}</div>
                            <input
                                type="radio"
                                value={option}
                                checked={
                                    i ===
                                    (this.value !== undefined
                                        ? this.value
                                        : response !== undefined
                                          ? response.data.value
                                          : data.hint)
                                }
                                readOnly={!!response}
                                onChange={this.onChangeValue}
                            />
                        </label>
                    ))}
                    {!response ? (
                        <StandardButton
                            large={true}
                            primary={true}
                            loading={this.loading}
                        >
                            Submit
                        </StandardButton>
                    ) : undefined}
                </Form>
            </div>
        );

        return (
            <SplitView
                className={styles["selection-request-details"]}
                view={<AssetImage src={data.imageUrl} />}
                summary={summary}
            />
        );
    }

    private onSubmit = async () => {
        const { request, onRespond } = this.props;
        const { data } = request;
        const value = this.value !== undefined ? this.value : data.hint;

        try {
            if (value !== undefined) {
                await eventStore.respond(request, {
                    value,
                });
            }
            onRespond();
        } catch (error) {
            captureError(error as Error);
        }
    };

    private onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
        const responses = this.props.request.responses;
        if (responses && responses.length > 0) {
            return;
        }
        this.value = this.props.request.data.options.indexOf(
            event.target.value,
        );
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
